import { FC, ReactNode, useId } from 'react'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Box, Chip, FormHelperText, Stack, Tooltip, Typography } from '@mui/material'

import { FILTER_CHIP_SX } from '@/common/styles'

import { FIELD_LABEL_SX } from '../styles'

interface ThemedFieldProps {
  label: string
  /**
   * The value or input. A function gets the id of the input, so that the label names it.
   * Other content (a value, or a group of inputs) is a group that the label names.
   */
  children: ReactNode | ((inputId: string) => ReactNode)
  required?: boolean
  /** More about the field, in a tooltip on an info icon next to the label. */
  info?: string | false
  error?: boolean
  errorText?: string
  hidden?: boolean
}

/**
 * One field of the policy form in the new Mobility theme: the label above its value or input,
 * and the error text below. The old form uses `UI.Field` (through `HideableField`).
 */
export const ThemedField: FC<ThemedFieldProps> = ({
  label,
  children,
  required = false,
  info,
  error = false,
  errorText,
  hidden = false,
}) => {
  const labelId = useId()
  const inputId = useId()

  if (hidden) return null

  const forInput = typeof children === 'function'

  return (
    <Stack spacing={0.75}>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography
          id={labelId}
          component={forInput ? 'label' : 'div'}
          htmlFor={forInput ? inputId : undefined}
          sx={FIELD_LABEL_SX}
        >
          {label}
          {required && (
            <Box component="span" aria-hidden sx={{ color: 'error.main', ml: 0.25 }}>
              *
            </Box>
          )}
        </Typography>
        {info && (
          <Tooltip title={info}>
            {/* `titleAccess` names the icon. Without it, MUI hides the icon from screen readers. */}
            <InfoOutlinedIcon
              titleAccess={info}
              tabIndex={0}
              fontSize="small"
              sx={{ color: 'text.secondary', fontSize: 16 }}
            />
          </Tooltip>
        )}
      </Stack>
      {forInput ? (
        children(inputId)
      ) : (
        <Box role="group" aria-labelledby={labelId}>
          {children}
        </Box>
      )}
      {error && errorText && <FormHelperText error>{errorText}</FormHelperText>}
    </Stack>
  )
}

/** A text value of the read-only form. */
export const ValueText: FC<{ children: ReactNode }> = ({ children }) => (
  <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
    {children}
  </Typography>
)

/**
 * Values of the read-only form as chips, with the same look as the page header's filter chips.
 * A long value (for example a geography name) is clipped, so the tooltip shows all of it.
 */
export const ValueChips: FC<{ values: string[] }> = ({ values }) => (
  <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 1 }}>
    {/* The index is part of the key: two values can have the same text, for example two
        operators without a name ("Unknown Operator"). */}
    {values.map((value, index) => (
      <Tooltip key={`${index}-${value}`} title={value}>
        <Chip size="small" label={value} sx={FILTER_CHIP_SX} />
      </Tooltip>
    ))}
  </Stack>
)
